import _ from 'lodash';
import container from '../container';
import FormManagerProxy from './form-manager-proxy';
import { BaseEventBus } from '@utilities/event-bus/event-bus';
import ControllersFactory from '../factories/controller-factory';
import { onStateChangeEvent, onValidationRequestEvent } from '../events';

const capitalizeFirst = str => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export default class FormManagerBase {
  constructor({ formIndex, defaultState, controllersHandlers = {} }) {
    if (!formIndex) throw new Error('Not formIndex provided');

    this.errors = {};
    this.formIndex = formIndex;
    this.bus = new BaseEventBus();
    this.defaultState = defaultState ?? {};
    this.state = defaultState ?? {};
    this.controllersConfigurations = {};
    container.registerEventBus(formIndex, this.bus);
    this.controllersHandlers = controllersHandlers ?? {};
    this.touchedFields = {}; //<- HashTable to track touched fields to reduce computations
    this.validate = this.validate.bind(this);
    this.manager = new FormManagerProxy(this);
    this.bus.listen(onValidationRequestEvent(formIndex), this.validate);

    this.bus.listen(onStateChangeEvent(formIndex), ({ name, value }) => {
      const handlerName = `on${capitalizeFirst(name)}StateChange`;

      if (handlerName in this.controllersHandlers) {
        this.controllersHandlers[handlerName]({ value, previous: this.state[name], manager: this.manager });
      }

      if ('onStateChange' in this.controllersHandlers) {
        this.controllersHandlers['onStateChange']({ value, previous: this.state[name], state: this.state, manager: this.manager });
      }

      this.touchedFields[name] = {
        ...this.touchedFields[name],
        isTouched: true,
      };

      this.state = { ...this.state, [name]: value };

      if ('onStateChangeAfter' in this.controllersHandlers) {
        this.controllersHandlers['onStateChangeAfter']({ value, previous: this.state[name], state: this.state, manager: this.manager });
      }
    });
  }

  resetFormState() {
    this.setData(this.defaultState);
    for (const { name, validationRules, defaultValue } of this.controllersConfigurations) {
      this.touchedFields[name] = {
        isTouched: false,
        rules: validationRules,
      };
      delete this.errors[name];
      this.setControlAsValid(name, defaultValue ?? '');
    }
  }

  setControllersHandlers(controllersHandlers) {
    this.controllersHandlers = controllersHandlers ?? {};
  }

  setVisibility(mode = 'edit') {
    const cmd = `${this.formIndex}:visibility-mode-*`;
    this.bus.dispatch(cmd, ensureMode(mode));
  }

  setControlVisibility(controlName, mode = 'edit') {
    if (!controlName) return;
    const cmd = `${this.formIndex}:${controlName}-visibility-mode`;
    this.bus.dispatch(cmd, ensureMode(mode));
  }

  validate({ name, value, rules }) {
    const validator = container.getValidator();
    if (validator) {
      const result = validator.execute({ name, value, rules });
      if (!result) return;
      if (result.isValid()) {
        this.notifyError({ name, error: false, message: '', givenValue: value });
        delete this.errors[name];
        this.setControlAsValid(name, value);
      } else {
        const message = result?.message ?? 'invalid field';
        this.errors[name] = { error: true, message };
        this.notifyError({ name, error: true, message, givenValue: value });
        this.setControlAsInvalid(name, message, value);
      }
      if ('onErrors' in this.controllersHandlers) {
        this.controllersHandlers['onErrors']({ errors: this.errors, manager: this.manager });
      }
    }
  }

  isFormFilledOK() {
    const validator = container.getValidator();
    if (validator) {
      for (const { name, validationRules } of this.controllersConfigurations) {
        const controllerActualState = this.state[name];
        const result = validator.execute({ name, value: controllerActualState, rules: validationRules });
        if (!result || result.isValid()) continue;
        else return false;
      }
    }
    return true;
  }

  notifyError({ name, error, message, givenValue }) {
    const handlerName = `on${capitalizeFirst(name)}Error`;
    if (handlerName in this.controllersHandlers) {
      this.controllersHandlers[handlerName]({ isInvalid: error, reason: message, givenValue, manager: this.manager });
    }
  }

  setControlAsInvalid(controlName, message, givenValue) {
    const cmd = `${this.formIndex}:${controlName}-validation-result`;
    this.bus.dispatch(cmd, { error: true, message, givenValue });
  }

  setControlAsValid(controlName, givenValue) {
    const cmd = `${this.formIndex}:${controlName}-validation-result`;
    this.bus.dispatch(cmd, { error: false, message: '', givenValue });
  }

  // T-45 This method need to be splitted, now doing: validation and check - the idea is ok, but need to be refactored
  isValid() {
    for (const [key, val] of Object.entries(this.touchedFields)) {
      const { isTouched, rules } = val;
      if (!isTouched && rules) {
        console.log('[ValidationUntouchedField]: ', key, this.state[key]);
        this.validate({ name: key, value: this.state[key], rules });
      }
    }
    return !Object.keys(this.errors).length;
  }

  initControllers(configurations) {
    this.controllersConfigurations = configurations;

    for (const { name, validationRules } of this.controllersConfigurations) {
      this.touchedFields[name] = {
        isTouched: false,
        rules: validationRules,
      };
    }

    return <ControllersFactory configs={configurations} formIndex={this.formIndex} defaultState={this.state} />;
  }

  setControllerState(name, value) {
    if ('onStateChange' in this.controllersHandlers) {
      this.controllersHandlers['onStateChange']({ value, previous: this.state[name], manager: this.manager });
    }
    this.state = { ...this.state, [name]: value };
    this.bus.dispatch(`${this.formIndex}:${name}-set-state`, value);
    if ('onStateChangeAfter' in this.controllersHandlers) {
      this.controllersHandlers['onStateChangeAfter']({ value, previous: this.state[name], manager: this.manager });
    }
  }

  getData() {
    return this.state;
  }

  setData(data) {
    this.state = data;
    for (const controller of this.controllersConfigurations) {
      const { name, accessKey, defaultValue, mapperFunc } = controller;
      let val = defaultValue;
      if (accessKey) val = _.get(data, accessKey, defaultValue);
      else val = _.get(data, name, defaultValue);
      if (_.isFunction(mapperFunc)) val = mapperFunc({ value: val, data });
      this.setControllerState(name, val);
    }
  }

  setFormMode(mode) {
    this.setVisibility(mode);
  }

  // TODO changeControllerProperty(controlName, cb);
  // TODO changeControlValue(controlName, cb);
}

function ensureMode(mode) {
  return !mode || !['view', 'edit'].includes(mode) ? 'edit' : mode;
}
